// The status of a newsletter
export type NewsletterStatus = 'concept' | 'sending' | 'sent' | 'failed';

// The status of a newsletter recipient
export type NewsletterRecipientStatus = 'queued' | 'sending' | 'sent' | 'delivered' | 'opened' | 'clicked' | 'failed' | 'unsubscribed' | 'complained';

// A user as shown on a newsletter
export interface NewsletterUser {
    id: number;
    name: string;
}

// A newsletter without its body, as shown in the overview
export interface NewsletterListItem {
    id: number;
    subject: string | null;
    preheader: string | null;
    status: NewsletterStatus;
    is_release: boolean;
    release_number: number | null;
    layout: number | null;
    sending_address: number | null;
    from_name: string | null;
    from_email: string | null;
    reply_to: string | null;
    recipient_count: number;
    sent_count: number;
    failed_count: number;
    delivered_count: number;
    opened_count: number;
    clicked_count: number;
    unsubscribed_count: number;
    complained_count: number;
    sent_at: string | null;
    sent_by: NewsletterUser | null;
    created_by: NewsletterUser | null;
    error_message: string | null;
    created: string;
    modified: string;
    deleted?: boolean;
}

// A full newsletter as edited in the editor
export interface Newsletter extends NewsletterListItem {
    body: string | null;
    audience: number | null;
    audience_conditions: AudienceCondition[];
}

// The source an audience is built on, the users source is only available in the admin environment
export type AudienceSource = 'contacts' | 'users';

// A sent release of the admin environment as shown on the dashboard of every user
export interface PlatformUpdate {
    id: number;
    release_number: number;
    subject: string | null;
    sent_at: string;
    html?: string;
}

// A reusable audience: a named set of conditions resolved again at every send
export interface NewsletterAudience {
    id: number;
    name: string;
    description: string | null;
    source: AudienceSource;
    conditions: AudienceCondition[];
    contact_count: number | null;
    is_valid: boolean;
    newsletter_count: number;
    deleted?: boolean;
    created: string;
    modified: string;
}

// Whether newsletters can be sent from the environment and the remaining quota
export interface NewsletterSendStatus {
    can_send: boolean;
    reason: 'custom_domain_required' | null;
    is_trial: boolean;
    trial_email_limit: number;
    trial_emails_remaining: number | null;
    daily_email_limit: number | null;
    daily_emails_remaining: number | null;
}

// A recipient of a sent newsletter
export interface NewsletterRecipient {
    id: number;
    contact: number | null;
    email: string;
    name: string | null;
    status: NewsletterRecipientStatus;
    error_message: string | null;
    sent_at: string | null;
    delivered_at: string | null;
    opened_at: string | null;
    clicked_at: string | null;
    failed_at: string | null;
    unsubscribed_at: string | null;
    complained_at: string | null;
    open_count: number;
    click_count: number;
}

// A single condition of the audience of a newsletter
export interface AudienceCondition {
    field: string;
    operator: string;
    value: any;
}

// A selectable value of an audience field
export interface AudienceFieldChoice {
    value: string | number;
    label: string;
}

// A field the audience can filter on, with its operators
export interface AudienceFieldOption {
    name: string;
    source: 'model' | 'lookup' | 'first_related' | 'labels' | 'custom_field' | 'organization_field' | 'organization_labels' | 'organization_custom_field' | 'user' | 'admin_contact_field' | 'admin_contact_labels' | 'admin_contact_custom_field';
    group: 'contact' | 'organization' | 'user' | 'admin_contact'; // The organization group holds the fields of the organization a person belongs to, the admin contact group the customer contact of an environment
    type: 'text' | 'choice' | 'number' | 'boolean' | 'date' | 'labels' | 'checkbox' | 'related';
    label: string;
    operators: string[];
    options?: AudienceFieldChoice[];
    field_type?: string;
}

// A resolved recipient of the audience as shown before sending
export interface AudiencePreviewRecipient {
    contact_id: number | null;
    user_id: number | null;
    name: string;
    entity_type: string | null;
    email: string;
}

// The operators which take no value
export const VALUELESS_OPERATORS = ['is_empty', 'is_not_empty', 'is_true', 'is_false'];

// The operators which take a list of values
export const LIST_OPERATORS = ['in', 'has_any', 'has_none'];

// Base path of the newsletter pages
export const NEWSLETTER_PATH = '/contacts/newsletters';

// Paths of the two tabs of the overview page
export const NEWSLETTER_OVERVIEW_PATH = `${NEWSLETTER_PATH}/overview`;
export const NEWSLETTER_AUDIENCES_PATH = `${NEWSLETTER_PATH}/audiences`;

// Returns the page a newsletter opens on, the editor for a concept and the report otherwise
export const newsletterPagePath = (newsletter: NewsletterListItem): string => (
    newsletter.status === 'concept' ? `${NEWSLETTER_PATH}/${newsletter.id}` : `${NEWSLETTER_PATH}/${newsletter.id}/report`
);

// Path of the email settings where the own domain is set up
export const EMAIL_SETTINGS_PATH = '/settings/features/email/settings';
